import React, { memo, useCallback, useEffect, useMemo } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  useFloating,
  offset,
  flip,
  shift,
} from "@floating-ui/react-dom-interactions";
import cn from "classnames";
import s from "../Hotspots.module.scss";
import { RichTextContent } from "../../../lib/storyblok/types/fieldtypes/richText";
import { StyleVariant } from "../../../lib/storyblok/types/shared";
import RichText from "../../Common/RichText";
import { useContentStyle } from "../../../hooks/useStyle";

export interface ItemProps {
  title: string;
  description: RichTextContent;
  style?: StyleVariant;
  coordinateX: number;
  coordinateY: number;
  _uid: string;
  onSpotClick?: (uid: string) => void;
  isActive?: boolean;
  variant?: "dot" | "list" | "desktop";
  index: number;
}

const connectorVariants = {
  grow: { scaleY: 1, transition: { duration: 0.16, ease: "easeOut" } },
  shrink: { scaleY: 0, transition: { duration: 0.14, ease: "easeIn" } },
};

const Item: React.FC<ItemProps> = memo(
  ({
    title,
    description,
    style = "white",
    coordinateX = 50,
    coordinateY = 50,
    _uid,
    onSpotClick,
    isActive = false,
    variant = "dot",
    index,
  }) => {
    const reduceMotion = useReducedMotion();
    const inverted = useContentStyle(style);

    const xPct = Math.max(0, Math.min(100, coordinateX));
    const yPct = Math.max(0, Math.min(100, coordinateY));
    const growUp = yPct > 50;

    const popVariants = useMemo(
      () => ({
        hidden: { opacity: 0, scale: 0.92, y: growUp ? -2 : 2 },
        visible: {
          opacity: 1,
          scale: 1,
          y: 0,
          transition: {
            type: "tween",
            ease: "easeOut",
            duration: 0.18,
            delay: reduceMotion ? 0 : 0.08,
          },
        },
      }),
      [growUp, reduceMotion]
    );

    const { x, y, strategy, reference, floating } =
      useFloating<HTMLButtonElement>({
        placement: growUp ? "top" : "bottom",
        middleware: [offset(48), flip(), shift()],
      });

    const handleClick = useCallback(() => {
      onSpotClick?.(_uid);
    }, [onSpotClick, _uid]);

    const keyHandler = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
        if (e.key === "Escape" && isActive) handleClick();
      },
      [handleClick, isActive]
    );

    useEffect(() => {
      if (!isActive || variant !== "desktop") return;

      const esc = (ev: KeyboardEvent) => {
        if (ev.key === "Escape") onSpotClick?.(_uid);
      };

      window.addEventListener("keydown", esc);
      return () => window.removeEventListener("keydown", esc);
    }, [isActive, variant, _uid, onSpotClick]);

    const handleMouseEnter = useCallback(() => {
      if (variant === "desktop" && !isActive) onSpotClick?.(_uid);
    }, [variant, isActive, onSpotClick, _uid]);

    const handleMouseLeave = useCallback(() => {
      if (variant === "desktop" && isActive) onSpotClick?.(_uid);
    }, [variant, isActive, onSpotClick, _uid]);

    const Dot = useMemo(
      () => (
        <motion.button
          ref={reference}
          type="button"
          className={cn(s.spot, s[style], {
            [s.active]: isActive,
            [s.inverted]: inverted,
          })}
          aria-label={`${title} hotspot`}
          aria-expanded={isActive}
          {...(variant !== "desktop" && { onClick: handleClick })}
          onKeyDown={keyHandler}
          whileHover={{ scale: 1.2 }}
          whileFocus={{ scale: 1.2 }}
          transition={{ type: "tween", duration: 0.22, ease: "circOut" }}
        >
          <div className={cn(s.pulse, s[style])} />
          <div className={cn(s.number)}>{index}</div>
        </motion.button>
      ),
      [
        reference,
        style,
        isActive,
        inverted,
        title,
        variant,
        handleClick,
        keyHandler,
        index,
      ]
    );

    if (variant === "dot") {
      return (
        <div
          className={cn(s.spotWrapper, { [s.active]: isActive })}
          style={{ left: `${xPct}%`, top: `${yPct}%` }}
        >
          {Dot}
        </div>
      );
    }

    if (variant === "list") {
      return (
        <motion.div
          className={cn(s.mobileSpotItem, s[style], {
            [s.active]: isActive,
            [s.inverted]: inverted,
          })}
          onClick={handleClick}
          onKeyDown={keyHandler}
          role="tab"
          tabIndex={0}
          aria-selected={isActive}
          initial={false}
        >
          <div className={cn(s.number, s[style])}>{index}</div>
          <div className={s.mobileSpotContent}>
            <div className={s.title}>{title}</div>
            {description && (
              <div className={s.contentDescription}>
                <RichText content={description} alignment={"alignLeft"} />
              </div>
            )}
          </div>
        </motion.div>
      );
    }

    return (
      <div
        className={cn(s.spotWrapper, { [s.active]: isActive })}
        style={{ left: `${xPct}%`, top: `${yPct}%` }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {Dot}

        <AnimatePresence>
          {isActive && (
            <motion.div
              key="connector"
              className={cn(
                s.connector,
                growUp ? s.connectorUp : s.connectorDown,
                s[style]
              )}
              variants={connectorVariants}
              initial="shrink"
              animate="grow"
              exit="shrink"
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {isActive && (
            <motion.div
              ref={floating}
              className={s.spotContent}
              variants={popVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
              role="dialog"
              aria-modal="false"
              aria-labelledby={`title-${_uid}`}
            >
              <span id={`title-${_uid}`} className={s.contentTitle}>
                {title}
              </span>

              {description && (
                <div className={s.contentDescription}>
                  <RichText content={description} alignment={"alignLeft"} />
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }
);

Item.displayName = "Item";
export default Item;
